import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useSubNavStore, type SubNavStore, type NavItem } from '@frontend/nav-primitives';
import { breakpoints } from '@frontend/responsiveness';
import { useWeTags } from '@frontend/scope';
import { useLocation, Link } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, styles, type HeadingProps } from '@frontend/design-system';
import { Header, type SubNavItem } from './navigational-header';

type RouterHeaderProps = Omit<HeadingProps, 'title' | 'children'> & {
  title: string | ((activePath?: NavItem['subNavItems'][number]) => React.ReactNode);
};

const selectNavItem = (currentRoot: string) => (state: SubNavStore) => state.parentNavData?.[currentRoot];

const hasSameSubPath = (a: string, b: string): boolean => {
  const aPath = a.split('/');
  const bPath = b.split('/');
  return aPath[2] === bPath[1];
};

const mapNavItemToSubNavItem = (item: NavItem['subNavItems'][number], root: string): SubNavItem => ({
  id: item.relativePath ?? item.path ?? '',
  label: item.label,
  to: item.path || `${root}/${item.relativePath}`,
  count: item.count,
  isPassiveCount: item.isPassiveCount,
});

export const RouterHeader = ({ title, ...rest }: RouterHeaderProps) => {
  const isSharedHeaderEnabled = useWeTags('shared-header');
  const currentPath = useLocation().pathname;

  const currentRoot = useMemo(() => {
    const [, ...pathParts] = currentPath?.split('/') ?? [];
    return `/${pathParts?.[0] ?? ''}`;
  }, [currentPath]);

  const navItem = useSubNavStore(useMemo(() => selectNavItem(currentRoot), [currentRoot]));

  const activePath = useMemo(
    () => navItem?.subNavItems?.find((item) => hasSameSubPath(currentPath, item?.relativePath ?? '')),
    [navItem?.subNavItems, currentPath]
  );

  const subNavItems = useMemo(
    () => navItem?.subNavItems?.map((item) => mapNavItemToSubNavItem(item, currentRoot)) ?? [],
    [navItem?.subNavItems, currentRoot]
  );

  const activeId = activePath?.relativePath ?? activePath?.path ?? '';

  const displayTitle = useMemo(() => {
    if (typeof title === 'function') {
      return title(activePath);
    } else if (typeof title === 'string') {
      return title;
    } else {
      return activePath?.label;
    }
  }, [title, activePath]);

  if (!isSharedHeaderEnabled) {
    return (
      <Heading
        level={1}
        {...rest}
        css={css`
          @container (max-width: ${breakpoints.small.max}px) {
            font-size: ${theme.fontSize(24)};
          }
          @container (min-width: ${breakpoints.small.max}px) {
            font-size: ${theme.fontSize(32)};
          }
        `}
      >
        <span css={styles.truncate}>{displayTitle}</span>
      </Heading>
    );
  }

  return (
    <Header.Root activeId={activeId} linkComponent={Link} {...rest}>
      <Header.MobileSubNav items={subNavItems}>
        <Header.Title textAlign={rest?.textAlign}>{displayTitle}</Header.Title>
        <Header.Badge count={activePath?.count} isPassive={activePath?.isPassiveCount} />
      </Header.MobileSubNav>
    </Header.Root>
  );
};

const headerStyles = css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(2, 2, 1, 2)};
  justify-content: space-between;
  margin: 0;
`;

export const RouterPanelHeader = ({ children, ...rest }: React.PropsWithChildren<RouterHeaderProps>) => (
  <header css={headerStyles}>
    <RouterHeader {...rest} />
    {children}
  </header>
);
